import { LinearProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import Pipedrive from "common/utils/pipedrive";
import { restoreToken } from "common/utils/token";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AnalyticsEvents, mixpanel } from "../../common/analytics";

import { useFormik } from "formik";

import { FullScreenDialog } from "common/components";
import { createUser, fetchTokenAndStore, initializeApp } from "services";
import { useAppStore } from "stores";
import FarmDetails from "./FarmDetails";
import Teaser from "./Teaser";
import UserDetails from "./UserDetails";
import Welcome from "./Welcome";

export interface FormValues {
  mail: string;
  name: string;
  phone: string;
  password: string;
  size: string;
  farmType: string;
  carbonFarmingInterest: string;
}

export default function SignupDialog() {
  const navigate = useNavigate();
  const location = useLocation();
  const creatingUser = useAppStore((state) => state.isLoggingIn);
  const appInitialising = useAppStore((state) => state.loading);

  const loading = creatingUser || appInitialising === "pending";
  const pipedriveApi = new Pipedrive();
  const formik = useFormik<FormValues>({
    initialValues: {
      mail: "",
      name: "",
      phone: "",
      password: "",
      size: "",
      farmType: "",
      carbonFarmingInterest: "",
    },
    onSubmit: () => {
      void handleSignup();
    },
  });
  // even if we don't have the language supported yet,
  // we still want people to be able to save their prefered language.
  // That way we know which missing translations we need to add.
  const language = navigator.language;

  restoreToken(location); // for now trying to restore token from url only on signup

  const handleSignup = async () => {
    mixpanel.track(AnalyticsEvents.SIGNUP_COMPLETED);

    try {
      await createUser({
        guid: "new",
        farms: [],
        name: formik.values.name,
        mail: formik.values.mail,
        phone: formik.values.phone,
        password: formik.values.password,
        lang: language,
      });
      mixpanel.track(AnalyticsEvents.INITIAL_LOGIN_STARTED);
      await fetchTokenAndStore({
        mail: formik.values.mail,
        password: formik.values.password,
      });
      void initializeApp();
    } catch (err) {
      console.log(err);
      mixpanel.track(AnalyticsEvents.INITIAL_LOGIN_FAILED);
    }
    if (process.env.NODE_ENV === "production") {
      // only add signups to pipedrive if we are in production
      try {
        const person = await pipedriveApi.addPerson({
          name: formik.values.name,
          email: formik.values.mail,
          phone: formik.values.phone,
          "689dac0343ab7408b80b11091d81cd85b78b0346": formik.values.farmType,
          "34f81b5dea3876fe0040f11ecd5ac00360c26718": formik.values.size,
          "863a8324f0ad1bc3624dcb01762a978ed980b546":
            formik.values.carbonFarmingInterest === "yes" ? "31" : "32",
        });

        await pipedriveApi.addDeal({
          title: `${formik.values.name} Deal`,
          person_id: person.data.id,
          stage_id: "25", // stage_id 25 is "account created"
        });
      } catch (err) {
        console.log(err);
      }
    }

    navigate("/signup/welcome");
  };

  return (
    <FullScreenDialog>
      {loading && <LinearProgress />}
      <Routes>
        <Route
          path="/"
          element={<Navigate to="/signup/get-started" replace />}
        />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/get-started" element={<Teaser />} />
        <Route
          path="/user-details"
          element={
            <UserDetails
              values={formik.values}
              handleChange={formik.handleChange}
            />
          }
        />
        <Route
          path="/farm-details"
          element={
            <FarmDetails
              values={formik.values}
              handleChange={formik.handleChange}
              handleSubmit={formik.handleSubmit}
            />
          }
        />
      </Routes>
    </FullScreenDialog>
  );
}

export const StyledForm = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  margin: "auto",
  width: "250px",
  paddingBottom: theme.spacing(2),
}));
